import type { CompanyDisplayRole } from "../utils/segment-connections"

import { cn } from "@/lib/utils"

import { marketColors } from "../data/mock-segment-analysis-data"
import { COMPANY_NAME } from "../utils/branding"
import { marketBubbleFrame } from "../utils/connections-graph-layout"
import { COMPANY_DISPLAY_ROLE_ORDER, COMPANY_FILL } from "../utils/segment-connections"

/** Interactive legend (#2071): role entries toggle company visibility, market
 * entries narrow the graph. Filtered-out entries render dimmed/struck so the
 * legend doubles as the filter state. */
export function ConnectionsGraphLegend({
  hiddenRoles,
  marketFilter,
  markets,
  onReset,
  onToggleMarket,
  onToggleRole,
}: {
  hiddenRoles: Set<CompanyDisplayRole>
  marketFilter: Set<string>
  markets: string[]
  onReset: () => void
  onToggleMarket: (marketName: string) => void
  onToggleRole: (role: CompanyDisplayRole) => void
}) {
  const hasFilters = hiddenRoles.size > 0 || marketFilter.size > 0
  return (
    <div className="border-border bg-card/90 text-muted-foreground absolute top-3 right-3 flex max-h-[calc(100%-1.5rem)] flex-col gap-1 overflow-y-auto rounded-md border px-2.5 py-2 text-[10px] shadow-sm backdrop-blur">
      <span className="text-foreground font-semibold">Markets · Segments · Companies</span>
      {COMPANY_DISPLAY_ROLE_ORDER.map(({ role, label }) => {
        const off = hiddenRoles.has(role)
        return (
          <button
            key={role}
            aria-pressed={!off}
            type="button"
            className={cn(
              "hover:text-foreground flex cursor-pointer items-center gap-1.5 text-left transition",
              off && "line-through opacity-40"
            )}
            title={
              off
                ? `Show ${label.toLowerCase()} companies`
                : `Hide ${label.toLowerCase()} companies`
            }
            onClick={() => onToggleRole(role)}
          >
            <span className="size-1.5 rounded-full" style={{ background: COMPANY_FILL[role] }} />
            {label}
          </button>
        )
      })}
      {markets.length > 1 ? (
        <>
          <span className="text-foreground mt-1 font-semibold">
            Filter markets <span className="font-normal opacity-70">(double ring on map)</span>
          </span>
          {markets.map((marketName) => {
            const active = marketFilter.size === 0 || marketFilter.has(marketName)
            const color = marketColors[marketName] ?? "#64748b"
            return (
              <button
                key={marketName}
                aria-pressed={marketFilter.has(marketName)}
                title={`Toggle ${marketName}`}
                type="button"
                className={cn(
                  "hover:text-foreground flex cursor-pointer items-center gap-1.5 text-left transition",
                  !active && "opacity-40"
                )}
                onClick={() => onToggleMarket(marketName)}
              >
                {/* Double-ring swatch mirrors the market bubble's frame (#3306 #14). */}
                <span
                  className="size-1.5 rounded-full"
                  style={{ background: color, ...marketBubbleFrame(color) }}
                />
                {marketName}
              </button>
            )
          })}
        </>
      ) : null}
      {hasFilters ? (
        <button
          className="text-primary mt-0.5 text-left font-semibold hover:underline"
          type="button"
          onClick={onReset}
        >
          Reset filters
        </button>
      ) : null}
      <span className="mt-0.5">Bubble size = {COMPANY_NAME} sales · drag to untangle</span>
      <span>Click a segment to open it · a company for its dossier</span>
    </div>
  )
}
